// QuestsApplets.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectAppletsFilledStateSelector,
  selectAvailableApplets,
} from 'modules/applets/selectors';

import QuestSection from './QuestSection';
import { QuestPortfolio } from 'models/QuestPortfolio';
import { ButtonIcon, ProfileSectionPanel, ProfileSectionSubTitle, StackContainerIcon } from '../../components';
import { QuestApplet } from 'models/QuestApplet';
import AppletPreviewModal from './AppletPreviewModal';
import AddFileModal from './AddFileModal';
import AddAppletModal from './AddAppletModal';
import AddRichTextModal from './AddRichTextModal';
import { QuestSectionItems } from './QuestSectionItems';
import useActionStatus from 'hooks/useActionStatus';
import { getQuestApplets, getRestQuestApplets } from 'modules/applets/actions';
import { selectLoadingAdditionalFinished } from 'modules/loading-screen/quests/selectors';
import { CenterLoader } from 'components/loading-spinner/components';
import LoadingSpinner from 'components/loading-spinner';
import theme from 'utils/theme';
import { AppDispatch } from 'redux/typings';
import { APPLETS_GET_CURRENT_CONTENT } from 'modules/applets/typings';
import { setAlphaDirection, toggleAlphaDirection, updateSortByQuest } from 'modules/profile/actions';
import { SORT_TYPE } from 'modules/profile/typings.d';
import { alphaDirectionSelect, sortModeSelect } from 'modules/profile/selectors';
import QuestAppletIcon from './QuestAppletIcon';
import CalendarIcon from './CalendarIcon';
import ArtifactTypeIcon from './ArtifactTypeIcon';
import AzAlpha from './AzAlpha';
import ZaAlpha from './ZaAlpha';

export type ModalState = {
  appletPreviewModalOpen?: boolean;
  addAppletModalOpen?: boolean;
  addFileModalOpen?: boolean;
  addRichTextModalOpen?: boolean;
  quest?: QuestPortfolio;
  applet?: QuestApplet;
};

const QuestsApplets = () => {
  const dispatch = useDispatch<AppDispatch>();
  const sortMode = useSelector(sortModeSelect);
  const questPortfolios = useSelector(selectAvailableApplets);
  const alphaDir = useSelector(alphaDirectionSelect);

  const alphaLabel = alphaDir === 'asc' ? 'Sort by A to Z' : 'Sort by Z to A';
  const alphaIcon = alphaDir === 'asc'
    ? (active: boolean) => <AzAlpha active={active} />
    : (active: boolean) => <ZaAlpha active={active} />;

  const MODES: { mode: SORT_TYPE; label: string; icon: (active: boolean) => JSX.Element }[] = [
    { mode: SORT_TYPE.QUEST, label: 'Sort by Quest', icon: (active) => <QuestAppletIcon active={active} /> },
    { mode: SORT_TYPE.DATE, label: 'Sort by Date', icon: (active) => <CalendarIcon active={active} /> },
    { mode: SORT_TYPE.TYPE, label: 'Sort by Type', icon: (active) => <ArtifactTypeIcon active={active} /> },
    { mode: SORT_TYPE.ALPHA, label: alphaLabel, icon: alphaIcon },
  ];

  const [isPendingAchievements, wasFulfilledAchievements] = useActionStatus(getQuestApplets);
  const [isPendingRestAchievements] = useActionStatus(getRestQuestApplets);
  const { appletsActiveQuestFilled, appletsRestQuestFilled } = useSelector(selectAppletsFilledStateSelector);
  const loadingFinished = useSelector(selectLoadingAdditionalFinished);

  const shouldShowLoader = useMemo(
    () =>
      !loadingFinished ||
      isPendingAchievements ||
      !wasFulfilledAchievements ||
      isPendingRestAchievements,
    [loadingFinished, isPendingAchievements, wasFulfilledAchievements, isPendingRestAchievements]
  );

  const [modalState, setModalState] = useState<ModalState>({});

  const openAppletPreviewModal = (applet: QuestApplet) => {
    setModalState({ appletPreviewModalOpen: true, applet });
  };

  const openAddAppletModal = () => {
    const defaultQuest = questPortfolios.find(q => q.isActiveQuest) ?? questPortfolios[0];
    setModalState({ addAppletModalOpen: true, quest: defaultQuest });
  };

  const openAddFileModal = (quest: QuestPortfolio) => {
    setModalState({ addFileModalOpen: true, quest });
  };

  const openAddRichTextModal = (quest: QuestPortfolio) => {
    setModalState({ addRichTextModalOpen: true, quest });
  };

  const closeModal = () => {
    setModalState({});
  };

  const handleChangeMode = (mode: SORT_TYPE) => {
    if (mode === SORT_TYPE.ALPHA) {
      if (sortMode === SORT_TYPE.ALPHA) {
        dispatch(toggleAlphaDirection());
      } else {
        dispatch(updateSortByQuest(SORT_TYPE.ALPHA));
        dispatch(setAlphaDirection('asc'));
      }
    } else {
      dispatch(updateSortByQuest(mode));
    }
  };


  useEffect(() => {
    if (loadingFinished && !wasFulfilledAchievements) {
      dispatch(getQuestApplets());
    }
  }, [wasFulfilledAchievements, loadingFinished, dispatch]);

  useEffect(() => {
    if (
      !isPendingRestAchievements &&
      appletsActiveQuestFilled &&
      !appletsRestQuestFilled
    ) {
      dispatch(getRestQuestApplets(APPLETS_GET_CURRENT_CONTENT.NO));
    }
  }, [appletsActiveQuestFilled, appletsRestQuestFilled, isPendingRestAchievements, dispatch]);

  return (
    <ProfileSectionPanel>

      <AppletPreviewModal
        open={modalState.appletPreviewModalOpen ?? false}
        applet={modalState.applet!}
        closeModal={closeModal}
      />
      <AddAppletModal
        open={modalState.addAppletModalOpen ?? false}
        quest={modalState.quest!}
        closeModal={closeModal}
        onAddFileClick={openAddFileModal}
        onAddRichTextClick={openAddRichTextModal}
      />
      <AddFileModal
        open={modalState.addFileModalOpen ?? false}
        quest={modalState.quest!}
        closeModal={closeModal}
      />
      <AddRichTextModal
        open={modalState.addRichTextModalOpen ?? false}
        quest={modalState.quest!}
        closeModal={closeModal}
      />

      <QuestSection onAddAppletClick={openAddAppletModal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <ProfileSectionSubTitle>{MODES.find(el => el.mode === sortMode)?.label}</ProfileSectionSubTitle>
          <StackContainerIcon>
            {MODES.map(({ mode, icon }) => (
              <ButtonIcon id={mode} key={mode} onClick={() => handleChangeMode(mode)}>
                {icon(sortMode === mode)}
              </ButtonIcon>
            ))}
          </StackContainerIcon>
        </div>
        <QuestSectionItems onAppletClick={openAppletPreviewModal} />

        {shouldShowLoader && (
          <CenterLoader>
            <LoadingSpinner size={50} color={theme.colors.blue1[100]} />
          </CenterLoader>
        )}
      </QuestSection>
    </ProfileSectionPanel>
  );
};

export default QuestsApplets;
